import { useRef, type FC } from "react";
import { X, Lightbulb, Camera, Maximize, Image as ImageIcon } from "lucide-react";
import { Button } from "../../components/ui/button";

interface CameraCaptureProps {
  onCapture: (imageUrl: string, file: File) => void;
  onClose: () => void;
}

export const CameraCapture: FC<CameraCaptureProps> = ({
  onCapture,
  onClose,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      onCapture(imageUrl, file);
    }
  };

  const openNativeCamera = () => {
    inputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 backdrop-blur-md">
      {/* Hidden Native Input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute right-4 top-4 z-[70] flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20"
      >
        <X size={24} />
      </button>

      {/* Photography Tips - Scrollable and Responsive */}
      <div className="relative w-full max-w-sm max-h-[75vh] overflow-y-auto overscroll-contain rounded-3xl bg-card p-4 shadow-2xl animate-in fade-in zoom-in duration-300 sm:p-6">
        <div className="mb-4 text-center sm:mb-6">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary sm:mb-4">
            <Camera className="h-6 w-6" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Photography Tips</h2>
          <p className="mt-1 text-sm text-muted-foreground sm:mt-2">
            Follow these tips for the best food analysis results
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start gap-4 rounded-xl border border-border bg-muted/50 p-3">
            <Lightbulb className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500" />
            <div>
              <h3 className="font-semibold text-foreground">Good Lighting</h3>
              <p className="text-xs text-muted-foreground">
                Use natural light or bright indoor lighting. Avoid harsh shadows.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 rounded-xl border border-border bg-muted/50 p-3">
            <Camera className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
            <div>
              <h3 className="font-semibold text-foreground">Top-Down Angle</h3>
              <p className="text-xs text-muted-foreground">
                Hold your phone directly above the food at a 45-90° angle.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 rounded-xl border border-border bg-muted/50 p-3">
            <Maximize className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
            <div>
              <h3 className="font-semibold text-foreground">Fill the Frame</h3>
              <p className="text-xs text-muted-foreground">
                Get close enough so the food fills most of the screen.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 rounded-xl border border-border bg-muted/50 p-3">
            <ImageIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-purple-500" />
            <div>
              <h3 className="font-semibold text-foreground">Clear Background</h3>
              <p className="text-xs text-muted-foreground">
                Use a clean plate or simple background for better recognition.
              </p>
            </div>
          </div>
        </div>

        <Button
          className="mt-6 w-full rounded-xl py-6 text-base font-semibold"
          onClick={openNativeCamera}
        >
          Got it, open camera
        </Button>
      </div>
    </div>
  );
};

export default CameraCapture;
